/**
 * parseUtcTimestamp
 *
 * Parse an ISO-8601 datetime string to epoch milliseconds, treating an
 * offset-less ("naive") string as UTC.
 *
 * Why this exists: the backend emits token-expiry timestamps from
 * `datetime.utcnow()`, whose `.isoformat()` has no timezone designator
 * (e.g. "2026-07-14T21:30:00.123456"). `new Date(that)` parses an offset-less
 * string as LOCAL time, so for a user west of UTC the perceived expiry is hours
 * later than reality and the silent refresh fires after the access token has
 * already died. Appending "Z" when no offset is present pins the intended UTC
 * interpretation. Strings that already carry a "Z" or a numeric offset are
 * parsed as-is. This also heals stale naive values written to localStorage by
 * builds from before the backend started emitting offset-aware strings.
 *
 * Returns NaN for empty/nullish input (callers guard on `!expiresAt` first).
 */
export function parseUtcTimestamp(value) {
  if (!value) return NaN;
  const s = String(value).trim();
  if (!s) return NaN;
  // Already offset-aware if it ends in Z/z or a +HH:MM / -HHMM style offset.
  // A trailing calendar day like "-14" is only two digits, so it does not match.
  const hasTimezone = /[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  return new Date(hasTimezone ? s : `${s}Z`).getTime();
}

export default parseUtcTimestamp;
